/**
 * cj-web 服务端、客户端通用 utils
 */

const axios = require("axios");
const { Base64 } = require("js-base64");
const config = require("./config");
const { NODE_ENV } = require("../env");
const routerSeparator = require("../app/const/routerSeparator");
const productSeparator = routerSeparator["/product"];
const psl = require("psl");
const serializeJavascript = require("serialize-javascript");
// const environment = this === window ? 'browser' : 'node';
const isServer = typeof window === "undefined";

function resDataProcess(data) {
  let _data = data;
  try {
    if (typeof data.result === "string") {
      _data.result = JSON.parse(data.result);
    }
  } catch (e) { }
  return _data;
}

/** axios 实例 */
function axiosEnhance(axios) {
  /**
   * url    接口地址
   * opts   body 体、或请求监听
   * listen 请求监听
   */
  return function _axiosFn(url, opts, listen) {
    if (!url) return;
    if (opts instanceof Function) {
      // 可变参处理
      _axiosFn(url, undefined, opts);
      return;
    }
    const cb = listen instanceof Function ? listen : () => { };
    return new Promise((resolve) => {
      cb(true);
      const data = opts ? { data: opts } : undefined;
      const thenFn = (res) => {
        cb(false);
        resolve(res);
      };
      axios(url, data).then(thenFn);
    });
  };
}

/** 处理 statusCode: "200" */
function statusCode200(data) {
  const errormsg = "api request error!";
  if (!data) {
    return [errormsg];
  }
  if (data.success) {
    return [null, data.data];
  }
  return +data.statusCode === 200
    ? [null, data.result]
    : [data.message || errormsg];
}

/** 修改类目接口的字段 vue和node公用 */
function renameCategoriesFields(catearr) {
  function fun(arr) {
    if (!arr || arr.length <= 0 || !(arr instanceof Array)) return [];
    return arr
      .map((_) => {
        const { dspId: id, nameEn, nameVal, nextLevel: children = [] } = _;
        const item = { id, className: nameEn?.replace(/('| |,|&)/g, ''), nameEn, nameVal, children };
        if (children && children.length > 0) {
          return { ...item, children: fun(children) };
        }

        return { ...item, children: [] };
      })
      .filter((_) => _.nameVal);
  }

  return fun(catearr).filter((_) => _.children.length > 0);
}

/**
 * 给客户端调用使用，node端不要使用
 */
class Axios {
  constructor(options) {
    // constructor 中 return 对象类型可以修改 new 实例
    // 可以实现方法的私有化
    // return 原始值类型不可以修改 new 实例
    return this.create(options);
  }

  create(options = {}) {
    const instance = axios.create({
      ...options,
    });

    this.interceptorsReq(instance);
    this.interceptorsRes(instance);
    // config 和 axios的一致，老代码继续改造
    return async (...config) => {
      if (isServer) {
        throw new Error("服务端禁止使用此请求函数");
      }
      const useVipPrefix = window.CjHeader.default.useVipPrefix || (({ url, SHOWMESSAGE, token }) => url);
      let _config = { ...options };
      // 匹配axios的两种入参模式
      if (typeof config[0] === "object") {
        _config = {
          ..._config,
          ...config[0],
          url: getDomainByUrl(useVipPrefix({ url: config[0].url })),
        };
      } else {
        _config = { ..._config, ...config[1], url: getDomainByUrl(useVipPrefix({ url: config[0] })) };
      }
      const res = await instance(_config);
      if (res.length === 1) {
        // 因为老项目原因，不管有没有报错都会返回resolve，但是可以通过res的长度判断是否有错误，为1是肯定是有错误
        const error = res[0];
        window.__Sentry__ &&
          window.__Sentry__.withScope((scope) => {
            scope.setTransactionName(_config.url);
            scope.setTags({
              apiUrl: _config.url,
              apiError: true,
              errorType: "client",
            });
            scope.setExtras({ request: _config, response: error });
            window.__Sentry__.captureMessage(
              `客户端接口请求抛错了-${_config.url} ${error}`,
              "warning"
            );
          });
        console.log("接口报错", JSON.stringify(res), JSON.stringify(_config));
      }
      return res;
    };
  }

  // 请求之前干点儿啥
  interceptorsReq(instance) {
    instance.interceptors.request.use(
      (config) => {
        let token =
          window.loginInfoController &&
          window.loginInfoController.info("token");
        // App WebView 兼容（iOS）：loginInfoController 缓存可能在 cookie 写入前已初始化，
        // 若缓存无 token，fallback 直接从 cookie 读取 cjLoginToken 或 _client_token。
        // 参考 CjHeader use-search.ts 中 osDeviceToken fallback 逻辑。
        if (!token) {
          const cjMatch = document.cookie.match(/cjLoginToken=([^;]+)/);
          const clientMatch = document.cookie.match(/_client_token=([^;]+)/);
          token = (cjMatch && cjMatch[1]) || (clientMatch && clientMatch[1]) || '';
        }
        if (token) config.headers.token = token;

        if (config.headers.platformToken) {
          config.headers.token = config.headers.platformToken
        }
        config.headers["cj-area"] = window.__AREACODE__;
        return config;
      },
      (error) => {
        return [error];
      }
    );
  }

  // 请求之后干点儿啥
  interceptorsRes(instance) {
    instance.interceptors.response.use(
      (response) => {
        // console.log("接口响应error:",response);
        clearLoginStoreInfo(response);
        // 判断是否是黑名单
        isBlackList(response);
        return [null, resDataProcess(response.data)];
      },
      (error) => {
        return [error];
      }
    );
  }
}

/**
 * 给客户端使用，为了方便调用
 * ⚠️ 客户端统一使用此方法，不要自己封装新的请求工具
 */
const c_ajax = new Axios();
/**
 * 给node调用使用，客户端禁止使用，客户端使用会抛错！！！
 * ⚠️ 服务端请使用 app/utils/request.js 封装的 ctx.$ajax 方法
 * ⚠️ 不要直接使用 n_ajax，而是在 BaseController 或 BaseService 中使用 this.ctx.$ajax
 */
const n_ajax = axios.create({
  headers: {
    Accept: "application/json;charset=utf-8",
  },
  timeout: 15000,
});

n_ajax.interceptors.request.use(
  function (config) {
    if (!isServer) {
      const mes = "客户端禁止使用此请求函数";
      // 监测客户端是否有调用，有调用就上报报错，需要及时修改
      window.__Sentry__ &&
        window.__Sentry__.withScope((scope) => {
          scope.setTags({
            apiUrl: config.url,
            codeError: true,
            errorType: "client",
          });
          window.__Sentry__.captureException(`${mes}-${config.url}`);
        });
      throw new Error(mes);
    }
    config.params && (config.params["_"] = +new Date());
    config.url && (config.url = getDomainByUrlSsr(config.url));
    return config;
  },
  function (error) {
    return Promise.reject(error);
  }
);

n_ajax.interceptors.response.use(
  (response) => {
    if (response.status === 200) {
      const { data, headers } = response;
      // 处理可能为null的data, 避免结构时出错
      if (data.data === null) data.data = {};

      return [null, resDataProcess(data), headers];
    }
    return response;
  },
  function (error) {
    // Do something with response error
    return [error];
  }
);

// 合并客户端和服务端请求，方便调用
// ⚠️ 使用规范：
//    - 客户端：直接使用 ajax 或通过 window.CjUtils.ajax
//    - 服务端：使用 ctx.$ajax (已在 BaseController 和 BaseService 中初始化)
//    - 不要自己封装新的请求工具，统一使用这两种方式
const ajax = isServer ? n_ajax : c_ajax;

/** url 匹配处理 */
function getDomainByUrl(url = "") {
  if (!url) {
    return url;
  }
  if (url.startsWith("http")) {
    return url;
  }
  var urlStart = url.split("/")[0];
  if (urlStart == "egg") {
    // egg对外接口
    return url.replace("egg", "");
  }
  if (urlStart == "pojo" || urlStart == "app") {
    urlStart = "app";
  }
  for (var k in config.api[NODE_ENV]) {
    if (k == urlStart) {
      return config.api[NODE_ENV][k] + url;
    }
  }
}

/** url 匹配处理 ssr端使用 */
function getDomainByUrlSsr(url = "") {
  if (!url) {
    return url;
  }
  if (url.startsWith("http")) {
    return url;
  }
  var urlStart = url.split("/")[0];
  if (urlStart == "pojo" || urlStart == "app") {
    urlStart = "app";
  }
  for (var k in config.apiNode) {
    if (k == urlStart) {
      return config.apiNode[k] + url;
    }
  }
}

/** 删除登录的一些信息 */
function clearLoginStoreInfo(response) {
  if (typeof window === "undefined") return;
  const noLogin =
    response &&
    response.data &&
    response.data.message == "Sorry, you are not logged in";
  if (!noLogin) return;
  console.error(
    "请求接口跳出登录=>",
    response.config.url,
    JSON.stringify(response.data)
  );
  window.__Sentry__ &&
    window.__Sentry__.withScope((scope) => {
      scope.setTags({
        apiUrl: response.config.url,
        apiError: true,
        errorType: "client",
        isToCJ: !!window.loginInfoController.info("loginfromerp"),
      });
      scope.setExtras({ request: response.config, response: response.data });
      window.__Sentry__.captureMessage(
        `请求接口跳出登录(是否tocj：${!!window.loginInfoController.info(
          "loginfromerp"
        )})`
      );
    });
  window.loginInfoController.clear();
  if (
    location.pathname.indexOf("my.html") > -1 ||
    location.pathname.indexOf("mine") > -1
  ) {
    return (location.href =
      "/login.html?target=" + $base64.encode(location.href));
  }
  location.reload();
}

/* 判断是否在黑名单 */
function isBlackList(response) {
  if (typeof window === "undefined") return;
  const _isBlackList =
    response &&
    response.data &&
    (Number(response.data.code) == 8880014 ||
      Number(response.data.statusCode) == 8880014);
  if (_isBlackList) {
    location.href = `${location.origin}/mine/intercept`;
    window.loginInfoController.clear();
  }
}

/** 安全的 JSONparse */
function JSONparse(jsonStr) {
  const res = [];
  try {
    res[0] = null;
    res[1] = JSON.parse(jsonStr);
  } catch (e) {
    res[0] = e;
  } finally {
    return res;
  }
}

/** base64 操作 */
const $base64 = {
  ...Base64,
  decode(str = "") {
    let r = str;
    try {
      r = Base64.decode(str);
    } catch (e) {
      console.warn("[base64.decode 报错]", str, "\n", e.stack);
    } finally {
      return r;
    }
  },
  encode(str = "") {
    let r = str;
    try {
      r = Base64.encode(str);
    } catch (e) {
      console.warn("[base64.encode 报错]", str, "\n", e.stack);
    } finally {
      return r;
    }
  },
};

/**
 * 考虑编码及体量控制，自定义 cookie 解析
 * 适用格式 a=1;b=2;c=3
 */
function cookieParser(cookie) {
  let r = null;
  if (!cookie) {
    return null;
  }
  try {
    const tmp = decodeURIComponent(cookie);
    const arr = tmp.split(";");
    const obj = {};
    arr.forEach((_) => {
      const t = _.split("=");
      obj[t[0]] = t[1];
    });
    r = obj;
  } finally {
    return r;
  }
}
/**
 * joson 拼装成 cookie 字符串
 * @param {JSON} cookie
 */
function cookieGenerator(cookie = {}) {
  const arr = Object.keys(cookie)
    .filter((k) => cookie[k])
    .map((k) => {
      const v = cookie[k];
      return `${k}=${v}`;
    });
  return arr.join(";");
}

function getDomainByUrlKey(url = "") {
  return config.api[NODE_ENV][url] || "";
}

/**
 * 获取客户端ip
 * @param {request} req 请求头
 */

function getClientIP(ctx) {
  return ctx.request.ip;
}

// TODO 启用geo内部ip判断，暂时不用
async function getClientInfoByIp(ip) {
  const res = await Promise.race([
    axios.get(`https://api.ip.sb/geoip/${ip}`),
    new Promise((resolve, reject) => {
      setTimeout(() => {
        // console.log('获取ip超时');
        resolve({});
      }, 2000);
    }),
  ]).catch((err) => { });
  const { data } = res || {};
  return data || {};
}

function getLngByCountry(country) {
  return {
    // 中国
    CN: "zh",
    // 美国
    US: "en",
    // 法国
    FR: "fr",
    // 德国
    DE: "de",
    // 印尼
    ID: "id",
    // 泰国
    TH: "th",
  }[country];
}

// 这个的客户端请求，不能用于服务端请求
function getTransWarehouse() {
  const config = {
    method: "post",
    url: "product-api/dbWorldInfo/v2/getWareHouseWorldsByLanguage",
    data: {},
  };
  return c_ajax(config);
}

function transWarehouse(target, arr = []) {
  let map = arr.reduce((obj, cur) => {
    let en = cur.en;
    if (!obj[en]) {
      obj[en] = cur["value"];
    }
    return obj;
  }, {});
  target &&
    target.forEach((v) => {
      v.areaVal = map[v.areaEn] || v.areaEn;
    });
  return target;
}

function getTopDomain(host = location.hostname) {
  const ip_pattern = /^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}$/; // 匹配ip
  if (ip_pattern.test(host)) {
    return host;
  }

  let domain = "";
  const p = psl.parse(host);
  if (p.listed) {
    domain = p.domain;
  } else {
    domain = host;
  }

  return domain === "localhost" ? domain : `.${domain}`;
}

/** 去重 */
function uniqueArr(arr = [], field) {
  var json = {};

  return arr.filter((item) => {
    if (json[item[field]]) {
      return false;
    } else {
      json[item[field]] = 1;
      return true;
    }
  });
}

// 将对象转化为URL参数
function objectToParams(paramObj) {
  const sdata = [];
  for (let attr in paramObj) {
    sdata.push(`${attr}=${encodeURIComponent(paramObj[attr])}`);
  }
  return sdata.join("&");
}
// 将URL参数转化为对象
function paramsToObject(paramObj) {
  if (!paramObj) {
    return {};
  }
  try {
    const searchParams = new URLSearchParams(paramObj);
    return Array.from(searchParams.entries()).reduce((obj, [key, value]) => {
      obj[key] = value;
      return obj;
    }, {});
  } catch (e) {
    return {};
  }
}

/**
 * 判断变量是否为无效的空值
 * @param {any} param
 * @returns {boolean}
 */
function isEmpty(param) {
  if (param === 0) {
    return false;
  }

  if (param) {
    if (typeof param === "object") {
      return JSON.stringify(param) === "[]" || JSON.stringify(param) === "{}";
    }
    return false;
  }

  return true;
}

/**
 * 当前浏览器是否支持webp格式, node默认返回false
 * @returns boolean
 */
function isSupportWebp() {
  if (typeof window === "undefined") return false;
  try {
    return (
      document
        .createElement("canvas")
        .toDataURL("image/webp")
        .indexOf("data:image/webp") == 0
    );
  } catch (err) {
    return false;
  }
}

/**删除标签且保留remainLen字符 */
function delHtmlTag(str, remainLen = 300) {
  if (typeof str != "string") return "";
  if (str.length <= 0) return "";
  return (
    str
      .replace(/<[^>]+>/g, "")
      .replace(/&nbsp;/g, " ")
      .substr(0, remainLen) + "..."
  ); //去掉所有的html标记
}

/**
 * 返回一个将js对象序列化成字符放入window指定值的script标签
 * @param {Object}} obj 传入要放入window变量的对象 { variableName: variableValue }
 * @returns script tag
 */
function createWindowVariables(obj, noScript = false) {
  let variables = "";
  const keys = Object.keys(obj);
  for (const key of keys) {
    const item = obj[key];
    try {
      variables += "\nwindow." + key + "=" + serializeJavascript(item);
    } catch (e) {
      variables += "";
      console.log(e, key, item);
    }
  }
  if (noScript) return variables;
  return "<script>" + variables + "\n" + "</script>";
}

/**
 * 格式化url规则, 可以是片段, 可以是整链接
 * @param {string} url 链接
 * @param {boolean} encode 是否encode编码url
 */
function urlQueryFormat(url = "", encode = true) {
  let _url = String(url);
  _url = _url.toLocaleLowerCase(); // 全小写
  function _(reg, value = "") {
    _url = _url.replace(reg, value);
  }
  _(/,/g);
  _(/( )?&/g); // 去掉空格&
  _(/'/g); // 去掉单引号
  _(/\?/g); // 去掉问号
  _(/ /g, "-"); // 空格转-
  _url = encode ? encodeURIComponent(_url) : decodeURIComponent(_url); // 是否编码
  return _url;
}

/**
 * 获取路由上展示需要的类目名称
 * @param {string} categoryName 类目名称英文
 * @returns 路由需要的参数 - 类目名称
 */
function getUrlCategoryNameFormat(categoryName = "") {
  return "wholesale-" + (urlQueryFormat(categoryName) || "all-categories");
}

/**
 * 获取正则匹的参数
 * @param {RegExp} reg 正则
 * @param {string} pathname 路由地址不带问号 如: /list/.......
 * @returns Promise 匹配的值
 */
function getPathnameParams(reg, pathname) {
  let args = [];
  args = pathname.match(reg);
  // 如果匹配失败，返回空数组
  if (!args) {
    return [];
  }
  // 去掉第一个参数
  args.shift();
  return args;
}

/** 递归类目生成对象map, 只针对类目 */
function getCategoriesMap(categories) {
  const valueMap = {};
  function loops(list, parent) {
    return (list || []).map(({ children, id, nameEn, nameVal }) => {
      const node = (valueMap[id] = {
        parent,
        id,
        nameEn,
        nameVal,
      });
      if (children) node.children = loops(children, node);
      return node;
    });
  }
  loops(categories);
  return valueMap;
}

/**
 * 获取拼接的地址
 * @param {object} options
 * @param {string} route 新路由地址
 * @param {Array<string>} params 新路由参数, 顺序排序
 * @param {Object} query 剩下的问号参数
 * @returns {string} 拼接后新的地址
 */
function getRedirectUrl({ route, params = [], query = {} }) {
  const otherQueryKeys = Object.keys(query);
  let remainQuerys = [];
  const mark = otherQueryKeys.length > 0 ? "?" : "";
  if (otherQueryKeys.length > 0) {
    otherQueryKeys.forEach(function (key) {
      const value = query[key];
      remainQuerys.push(key + "=" + value);
    });
  }

  const url = `${route}${params.length > 0 ? "/" : ""}${params.join(
    "/"
  )}${mark}${remainQuerys.join("&")}`;
  return url;
}

// 商品来源标识枚举
const PRODUCTFROM = {
  // home页面的Trending Products
  home_trending_products: "HTP",
  // home页面的News Products
  home_news_products: "HNP",
  // home页面的Super Deals
  home_super_deals: "HSD",
  // home页面的Video Products
  home_video_products: "HVP",
  // home页面的Print on Demand
  home_print_on_demand: "HPOD",
  // home页面的Global Warehouses
  home_global_warehouses: "HGW",
  // home页面的Recommended Products
  home_recommended_products: "HRP",
  // home页面的Brand Section
  home_brand_section: "HBS",
  home_explore_interests: "EIP",
  /** home页面的全球仓模块 */
  home_global_warehouse_module: "HGWM",
};

/**
 * 获取商品链接
 * @param {Object} options
 * @param {string} options.name 商品名称 英文
 * @param {string} options.id 商品id
 * @param {string} options.from 商品来自那个板块（约定的）
 * @param {Object} options.querys 问号参数
 * @returns {string} 商品详情链接
 */
function getProductHref({ name, id, from, querys = {} }) {
  // 不符合枚举的直接过滤，防止乱用
  const matched = !!Object.entries(PRODUCTFROM).find(function (p) {
    return p[1] === from;
  });

  // 过滤掉undefined对象的value
  function filterUndefinedValue(obj) {
    return Object.keys(obj).filter((key) => !!obj[key]);
  }
  const queryObj = { ...querys, from: matched ? from : undefined };
  const queryStrs = filterUndefinedValue(queryObj).map(
    (key) => `${key}=${encodeURIComponent(queryObj[key])}`
  );

  return `/product/${urlQueryFormat(name || "")}${productSeparator}${id}.html${queryStrs.length > 0 ? `?${queryStrs.join("&")}` : ""
    }`;
}

/**
 * 协助过滤values中存在的字符串undefined的数据
 * @param {any} values
 * @returns {any}
 */
function undefinedFilter(values) {
  if (Array.isArray(values)) {
    // 是数组的情况
    return values.map((i) => undefinedFilter(i));
  } else if (typeof values === "object") {
    // 是对象的情况
    return Object.keys(values).reduce((a, b) => {
      return { ...a, [b]: undefinedFilter(values[b]) };
    }, {});
  } else {
    if (values === "undefined") {
      return undefined;
    }
    return values;
  }
}

/**
 * 过滤掉null和undefined的值
 * @param {any} obj
 * @returns {any}
 */
function filterNullUndefined(obj) {
  if (!obj || typeof obj !== 'object') {
    return obj;
  }

  // Handle arrays
  if (Array.isArray(obj)) {
    return obj.map(item => filterNullUndefined(item));
  }

  // Handle objects
  return Object.entries(obj).reduce((acc, [key, value]) => {
    // If value is not null or undefined, keep the key-value pair
    if (value !== null && value !== undefined) {
      // Recursively filter if value is an object or array
      acc[key] = typeof value === 'object' ? filterNullUndefined(value) : value;
    }
    return acc;
  }, {});
}

/**
 * 输入的值不是数字则自动置为空，防止搜索失效
 * @param {any} val
 * @returns {any}
 */
function getNumber(val) {
  const isNumber = typeof +val === "number" && !isNaN(+val);
  if (isNumber) return val;
}

/**
 ** 乘法函数，用来得到精确的乘法结果
 ** 说明：javascript的乘法结果会有误差，在两个浮点数相乘的时候会比较明显。这个函数返回较为精确的乘法结果。
 ** 调用：accMul(arg1,arg2)
 ** 返回值：arg1乘以 arg2的精确结果
 **/
function accMul(arg1, arg2) {
  var m = 0,
    s1 = arg1.toString(),
    s2 = arg2.toString();
  try {
    m += s1.split(".")[1].length;
  } catch (e) { }
  try {
    m += s2.split(".")[1].length;
  } catch (e) { }
  return (
    (Number(s1.replace(".", "")) * Number(s2.replace(".", ""))) /
    Math.pow(10, m)
  );
}
/**
 ** 浮点数相加函数，用来得到精确的浮点数相加结果
 ** 说明：javascript的浮点数加法结果会有误差。
 ** 调用：addFloat(arg1, arg2)
 ** 返回值：arg1加arg2的精确结果
 **/
function addFloat(arg1, arg2) {
  var r1, r2, m, n;
  try {
    r1 = arg1.toString().split(".")[1].length;
  } catch (e) {
    r1 = 0;
  }
  try {
    r2 = arg2.toString().split(".")[1].length;
  } catch (e) {
    r2 = 0;
  }
  m = Math.pow(10, Math.max(r1, r2));
  n = r1 >= r2 ? r1 : r2;
  return ((arg1 * m + arg2 * m) / m).toFixed(n);
}
/**
 ** 浮点数相减函数，用来得到精确的浮点数相加结果
 ** 说明：javascript的浮点数减法结果会有误差。
 ** subtrFloat(arg1, arg2)
 ** 返回值：arg1减arg2的精确结果
 **/
function subtrFloat(arg1, arg2) {
  var r1, r2, m, n;
  try {
    r1 = arg1.toString().split(".")[1].length;
  } catch (e) {
    r1 = 0;
  }
  try {
    r2 = arg2.toString().split(".")[1].length;
  } catch (e) {
    r2 = 0;
  }
  m = Math.pow(10, Math.max(r1, r2));
  //动态控制精度长度
  n = r1 >= r2 ? r1 : r2;
  return ((arg1 * m - arg2 * m) / m).toFixed(n);
}

// 搜索的参数解析统一方法
function searchValueParse(keyWord) {
  return keyWord
    .trim()
    .replace(/\+/g, " ")
    .replace(/\.html$/, "");
}

/**
 * 注意：使用这个函数会把原来的路径上query自动带到最终的url上，需要的时候才使用
 * 设置路由参数，会自动覆盖同名称query参数，otherQuerys自动覆盖自带的路由query
 * @param {ctx} ctx
 * @param {string} appUrl 要跳转的app路由，不要带query参数
 * @param {object} options add: app跳转需要增加的参数， ft：app跳转需要过滤掉的参数，only：app跳转仅保留的参数（不包括add的参数）
 * @returns {string} 返回app带query参数链接
 */
const setQuerys = (ctx, appUrl, options = {}) => {
  const { add = {}, ft = [], only = [] } = options;
  let originalQuery = Object.keys(ctx.query).reduce((a, b) => {
    // only优先生效，如果only生效，ft就会失效
    if (only.length > 0) {
      if (only.includes(b)) return { ...a, [b]: ctx.query[b] };
      return a;
    }
    if (ft.length > 0 && ft.includes(b)) return a;
    return { ...a, [b]: ctx.query[b] };
  }, {});
  let query = filterNullUndefined({ ...originalQuery, ...add });
  const searchParams = new URLSearchParams(query)
  const queryStr = searchParams.toString();
  return `${appUrl}${queryStr ? `?${queryStr}` : ""}`;
};

module.exports = {
  $base64,
  Axios,
  axiosEnhance,
  ajax,
  c_ajax,
  getDomainByUrl,
  getDomainByUrlSsr,
  JSONparse,
  statusCode200,
  cookieParser,
  cookieGenerator,
  getDomainByUrlKey,
  getClientIP,
  getClientInfoByIp,
  getLngByCountry,
  NODE_ENV,
  getTransWarehouse,
  transWarehouse,
  getTopDomain,
  uniqueArr,
  objectToParams,
  paramsToObject,
  isEmpty,
  isSupportWebp,
  renameCategoriesFields,
  delHtmlTag,
  createWindowVariables,
  urlQueryFormat,
  getUrlCategoryNameFormat,
  getPathnameParams,
  getCategoriesMap,
  getRedirectUrl,
  getProductHref,
  undefinedFilter,
  PRODUCTFROM,
  getNumber,
  accMul,
  addFloat,
  subtrFloat,
  searchValueParse,
  setQuerys,
};
